import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar, Container, Button, Dropdown, Form } from 'react-bootstrap';
import { FiMenu, FiSun, FiMoon, FiLogOut, FiUser, FiChevronDown } from '../../utils/icons';
import { useInterfaceStore, TipoEmpresa } from '../../stores/useInterfaceStore';
import api from '../../services/api';
import styles from './Header.module.css';

interface IUsuarioLocal {
  nome?: string;
  email?: string;
}

interface EmpresaAcessivel {
  id: number;
  razao_social: string;
  nome_fantasia: string;
  tipo: TipoEmpresa;
}

const iniciaisDe = (nome?: string, email?: string): string => {
  const base = (nome || email || '?').trim();
  const partes = base.split(/\s+/).filter(Boolean);
  if (partes.length >= 2) return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
};

const Header: React.FC = () => {
  const { toggleSidebar, theme, toggleTheme, clearSession, empresaId, setEmpresaId, setTipoEmpresa } = useInterfaceStore();
  const navigate = useNavigate();

  const usuario: IUsuarioLocal = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('usuarioData') || '{}'); } catch { return {}; }
  }, []);

  const iniciais = iniciaisDe(usuario.nome, usuario.email);

  const [nomeEmpresa, setNomeEmpresa] = useState('');
  useEffect(() => {
    if (!empresaId) { setNomeEmpresa(''); return; }
    api.getCompany(empresaId).then(res => {
      if (res.success && res.data) {
        setNomeEmpresa(res.data.nome_fantasia || res.data.razao_social || '');
      }
    });
  }, [empresaId]);

  // Empresas que o usuário logado tem permissão de acessar (mesma regra de escopo de
  // GET /api/empresas — hierarquia Consultoria→clientes, Grupo→filiais, 4Comply→todas).
  // Carregada uma vez: esse conjunto não muda durante a sessão, só muda qual delas está
  // ativa — só um reload completo após o login reflete uma mudança de permissão.
  const [empresasAcessiveis, setEmpresasAcessiveis] = useState<EmpresaAcessivel[]>([]);
  const [buscaEmpresa, setBuscaEmpresa] = useState('');
  useEffect(() => {
    api.getCompanies().then(res => {
      if (res.success && Array.isArray(res.data)) setEmpresasAcessiveis(res.data);
    });
  }, []);

  const empresasFiltradas = empresasAcessiveis.filter(e => {
    const termo = buscaEmpresa.trim().toLowerCase();
    if (!termo) return true;
    return (e.nome_fantasia || '').toLowerCase().includes(termo)
      || (e.razao_social || '').toLowerCase().includes(termo);
  });

  // Troca a empresa ativa e recarrega a aplicação do zero (navegação completa, não só
  // atualização de estado) — garante que toda tela remonta já com o header
  // X-Empresa-Ativa novo, em vez de continuar mostrando dados em cache da empresa
  // anterior em alguma tela que não tivesse sido auditada pra reagir à troca.
  const handleTrocarEmpresa = (empresa: EmpresaAcessivel) => {
    if (empresa.id === empresaId) return;
    setEmpresaId(empresa.id);
    setTipoEmpresa(empresa.tipo);
    window.location.href = '/app/dashboard';
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('usuarioData');
    clearSession();
    navigate('/login');
  };

  return (
    <Navbar expand={false} className={styles.headerNavbar}>
      <Container fluid className="px-3 d-flex justify-content-between align-items-center">
        <Button
          variant="outline-secondary"
          onClick={toggleSidebar}
          className={styles.menuButton}
          aria-label="Abrir menu"
        >
          <FiMenu size={18} />
        </Button>

        {nomeEmpresa && (
          empresasAcessiveis.length > 1 ? (
            <Dropdown className={styles.empresaDropdown} onToggle={(isOpen) => { if (isOpen) setBuscaEmpresa(''); }}>
              <Dropdown.Toggle as="button" className={styles.empresaToggle} id="empresa-ativa-toggle" title="Trocar empresa ativa">
                {nomeEmpresa} <FiChevronDown size={14} />
              </Dropdown.Toggle>
              <Dropdown.Menu className={styles.empresaMenu}>
                <div className={styles.empresaMenuHeader}>Você está trabalhando na empresa:</div>
                <div className={styles.empresaMenuSearchWrap}>
                  <Form.Control
                    size="sm"
                    autoFocus
                    placeholder="Buscar empresa..."
                    value={buscaEmpresa}
                    onChange={e => setBuscaEmpresa(e.target.value)}
                    onClick={e => e.stopPropagation()}
                  />
                </div>
                <div className={styles.empresaMenuList}>
                  {empresasFiltradas.map(e => (
                    <Dropdown.Item
                      key={e.id}
                      active={e.id === empresaId}
                      onClick={() => handleTrocarEmpresa(e)}
                    >
                      {e.nome_fantasia || e.razao_social}
                      {e.tipo && <span className={styles.empresaMenuItemTipo}> ({e.tipo})</span>}
                    </Dropdown.Item>
                  ))}
                  {empresasFiltradas.length === 0 && (
                    <div className={styles.empresaMenuEmpty}>Nenhuma empresa encontrada.</div>
                  )}
                </div>
              </Dropdown.Menu>
            </Dropdown>
          ) : (
            <div className={styles.empresaAtiva}>{nomeEmpresa}</div>
          )
        )}

        <div className="d-flex align-items-center gap-2 ms-auto">
          <button
            type="button"
            className={styles.themeToggle}
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
            title={theme === 'dark' ? 'Tema claro' : 'Tema escuro'}
          >
            {theme === 'dark' ? <FiSun size={16} /> : <FiMoon size={16} />}
          </button>

          <Dropdown align="end">
            <Dropdown.Toggle as="button" className={styles.avatarToggle} id="user-avatar-toggle" title={usuario.nome || usuario.email || 'Usuário'}>
              {iniciais || <FiUser size={16} />}
            </Dropdown.Toggle>
            <Dropdown.Menu className={styles.avatarMenu}>
              <div className={styles.avatarMenuHeader}>
                <div className={styles.avatarMenuName}>{usuario.nome || 'Usuário'}</div>
                {usuario.email && <div className={styles.avatarMenuEmail}>{usuario.email}</div>}
              </div>
              <Dropdown.Divider />
              <Dropdown.Item onClick={handleLogout} className={styles.avatarMenuItem}>
                <FiLogOut size={15} /> Sair
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </Container>
    </Navbar>
  );
};

export default Header;
